import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Paper, Divider, Skeleton, Alert, TextField, Stack, Button, CircularProgress } from '@mui/material';
import InvoiceTable from '../../../components/Tables/InvoiceTable';
import { getInvoices } from '../../../utils/api';

export default function BusinessInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 15;

  const shopId = localStorage.getItem('shopId');

  useEffect(() => {
    if (page === 1) setLoading(true); else setLoadingMore(true);
    setError('');
    getInvoices(page, pageSize)
      .then(invRes => {
        const { results, count, next } = invRes.data || {};
        // Map company_id to shopId for each invoice
        const mappedInvoices = (results || []).map(inv => ({
          ...inv,
          shopId: inv.shopId || inv.company_id,
        }));
        const filteredByShop = mappedInvoices.filter(inv => inv.shopId === shopId);

        if (page === 1) {
          setInvoices(filteredByShop);
        } else {
          setInvoices(prev => [...prev, ...filteredByShop]);
        }
        setTotalInvoices(count || 0);

        // Determine if there are potentially more pages
        const apiHasNext = Boolean(next);
        const pageLikelyFull = (results || []).length === pageSize;
        setHasMore(apiHasNext || pageLikelyFull);
      })
      .catch(() => setError('Failed to load invoices'))
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, [shopId, page]);

  // Filtering Logic: Only by purchase date
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (filters.purchaseDate && inv.date !== filters.purchaseDate) return false;
      return true;
    });
  }, [invoices, filters]);

  // Pagination logic
  const paginatedInvoices = filteredInvoices.slice(0, page * pageSize);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [filters.purchaseDate]);

  // Custom minimal filter bar
  const handleDateChange = (e) => {
    setFilters({ purchaseDate: e.target.value });
  };
  const handleClear = () => setFilters({});

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 6, boxShadow: 0, border: '1px solid', borderColor: 'divider', maxWidth: 1100, width: '100%', mx: 'auto', background: 'background.paper' }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" mb={1}>
          Invoices
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {loading && page === 1 ? (
          <Skeleton variant="rectangular" height={300} />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', width: '100%' }} elevation={0}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" width="100%">
                <TextField
                  label="Purchase Date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.purchaseDate || ''}
                  onChange={handleDateChange}
                  sx={{ minWidth: 180, width: { xs: '100%', sm: 220 } }}
                />
                <Box flex={1} />
                <TextField
                  label="Clear"
                  type="button"
                  value="Clear Filters"
                  onClick={handleClear}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 120, width: { xs: '100%', sm: 140 }, cursor: 'pointer' }}
                  inputProps={{ style: { cursor: 'pointer' }, readOnly: true }}
                />
              </Stack>
            </Paper>
            {filteredInvoices.length === 0 ? (
              <Alert severity="info">No invoices found for your filters.</Alert>
            ) : (
              <>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <InvoiceTable rows={paginatedInvoices} minimal businessView />
                </Box>
                {paginatedInvoices.length > 0 && hasMore && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button 
                      variant="contained" 
                      onClick={() => setPage(page + 1)}
                      disabled={loadingMore}
                      startIcon={loadingMore ? <CircularProgress color="inherit" size={18} /> : null}
                    >
                      {loadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
} 
